"use client"

import { useState } from "react"
import { NavLink, useLocation } from "react-router-dom"
import { Box, Flex, Text, Tooltip, Icon, IconButton, Image } from "@chakra-ui/react"
import {
  FaChartBar,
  FaLightbulb,
  FaBullseye,
  FaLayerGroup,
  FaPercent,
  FaClipboardList,
  FaUsers,
  FaBoxOpen,
  FaStore,
  FaFileUpload,
  FaExchangeAlt,
  FaTrashAlt,
  FaClipboardCheck,
  FaServer,
  FaCheckDouble,
  FaFileAlt,
  FaBell,
  FaUser,
  FaHistory,
  FaAngleDoubleLeft,
  FaAngleDoubleRight,
} from "react-icons/fa"

import SupplierNavigation from "../SupplierNavigation"
import { neutral, brand, type, radius, layout, motion } from "../../theme/tokens"
import { resolveBrandMark } from "../../tools/brandMark"

// Nav is data, not markup. The previous layout repeated the same 12-line
// <Link><Box><Icon><Text> block seventeen times, which is why icons had drifted
// (Sales and Margin shared one, five admin pages shared FaFileImport).
const ANALYTICS = [
  { to: "/sales", label: "Sales", icon: FaChartBar, page: "Sales", restrictable: true },
  { to: "/insights", label: "Insights", icon: FaLightbulb, page: "Insights", restrictable: true },
  { to: "/strike", label: "Strike Rate", icon: FaBullseye, page: "StrikeRate", restrictable: true },
  { to: "/ranging", label: "Ranging", icon: FaLayerGroup, page: "Ranging", restrictable: true },
  { to: "/margin", label: "Margin", icon: FaPercent, page: "Margin", restrictable: true },
  { to: "/orders", label: "Orders", icon: FaClipboardList, adminOnly: true },
]

// The admin side of the rail, in four themed groups (TASK-admin-panel-revamp):
// each renders as its own NavGroup and disappears entirely when the account
// can see none of its items.
// camPage: masters always see those items; a CAM sees them when granted that
// page (the scoped-admin capabilities from TASK-user-creation-rework — the API
// side is requireAdminScoped, which also scopes what they can touch).
const PEOPLE_ACCESS = [
  // Organisations + supplier/CAM logos live inside the Users page's views now.
  { to: "/users", label: "Users", icon: FaUsers, page: "Users" },
]

const DATA_ADMIN = [
  { to: "/articles", label: "Articles", icon: FaBoxOpen, page: "Articles" },
  { to: "/stores", label: "Stores", icon: FaStore, page: "Stores" },
  { to: "/imports", label: "Imports", icon: FaFileUpload, page: "Imports" },
  { to: "/delete", label: "Delete", icon: FaTrashAlt, page: "Delete" },
  { to: "/data-status", label: "Data Status", icon: FaClipboardCheck, adminOnly: true, camPage: "DataStatus" },
  { to: "/validation", label: "Validation", icon: FaCheckDouble, adminOnly: true, camPage: "Validation" },
]

const EDI_AUTOMATION = [
  { to: "/edi", label: "EDI", icon: FaExchangeAlt, page: "Edi" },
  { to: "/pnp-ftp", label: "PnP FTP", icon: FaServer, adminOnly: true },
  { to: "/alerts", label: "Alerts", icon: FaBell, adminOnly: true, camPage: "Alerts" },
]

const REPORTS_ADMIN = [
  // The org admins' report sender — same "Batch Reports" name as the master
  // item by request. orgOnly gates it to org_admin accounts ONLY (masters have
  // their own batch reports below, and a CAM granted 'Reports' must not see
  // this one — it drives /org-admin/* endpoints).
  { to: "/org-reports", label: "Batch Reports", icon: FaFileAlt, page: "Reports", orgOnly: true },
  { to: "/batch-reports", label: "Batch Reports", icon: FaFileAlt, adminOnly: true, camPage: "Reports" },
]

const ACCOUNT = [
  // spearOnly: accounts belonging to a partner organisation never get My
  // Profile (it carries billing/subscription/password concerns Spear manages
  // for them) — their org admin manages their account instead.
  { to: "/myProfile", label: "My Profile", icon: FaUser, page: "MyProfile", spearOnly: true },
  { to: "/loginhistory", label: "History", icon: FaHistory, page: "LoginHistory" },
]

const NavItem = ({ item, isActive, collapsed }) => {
  const content = (
    <Flex
      as={NavLink}
      to={item.to}
      align="center"
      gap={2.5}
      h="34px"
      px={collapsed ? 0 : 2.5}
      justify={collapsed ? "center" : "flex-start"}
      borderRadius={radius.sm}
      position="relative"
      // 0.78 rather than 0.66: the rail lightens toward its foot, and at 0.66
      // the last few labels fell under 4.5:1 against it.
      color={isActive ? neutral[0] : "rgba(255,255,255,0.78)"}
      bg={isActive ? "rgba(255,255,255,0.12)" : "transparent"}
      fontSize={type.size.small}
      fontWeight={isActive ? type.weight.semibold : type.weight.regular}
      transition={`background-color ${motion.fast}, color ${motion.fast}`}
      _hover={{ bg: "rgba(255,255,255,0.09)", color: neutral[0] }}
      _focusVisible={{ outline: `2px solid rgba(255,255,255,0.7)`, outlineOffset: "-2px" }}
    >
      {/* Active marker reads as a tab indicator rather than a filled pill. */}
      {isActive && (
        <Box
          position="absolute"
          left={collapsed ? "6px" : "-8px"}
          top="7px"
          bottom="7px"
          w="3px"
          borderRadius="2px"
          bg={brand[300]}
        />
      )}
      <Icon as={item.icon} boxSize="14px" flexShrink={0} opacity={isActive ? 1 : 0.85} />
      {!collapsed && <Text whiteSpace="nowrap">{item.label}</Text>}
    </Flex>
  )

  if (!collapsed) return content
  return (
    <Tooltip label={item.label} placement="right" openDelay={200}>
      <Box>{content}</Box>
    </Tooltip>
  )
}

const NavGroup = ({ title, items, pathname, collapsed }) => {
  if (items.length === 0) return null
  return (
    <Box mb={4}>
      {!collapsed && (
        <Text
          px={2.5}
          mb={1.5}
          fontSize={type.size.micro}
          fontWeight={type.weight.semibold}
          letterSpacing={type.tracking.label}
          textTransform="uppercase"
          color="rgba(255,255,255,0.42)"
        >
          {title}
        </Text>
      )}
      <Flex direction="column" gap="1px" pl={collapsed ? 0 : 2}>
        {items.map((item) => (
          <NavItem key={item.to} item={item} isActive={pathname === item.to} collapsed={collapsed} />
        ))}
      </Flex>
    </Box>
  )
}

const Sidebar = ({ collapsed, onToggleCollapse, userPageAccess, restrictedyn, hasSuppliers, isAdmin }) => {
  const { pathname } = useLocation()

  // Resolved from the signed-in account, so it is fixed for the session: the
  // rail carries the partner's mark for every supplier they can select, not
  // Spear's. Read on each render rather than held in state — sign-in and
  // sign-out both remount this tree, and there is no other moment it changes.
  //
  // An org-branded mark (resolved.candidates) is tried source-by-source; when
  // no org logo exists anywhere the rail falls back to the legacy mark, so an
  // org without a logo looks exactly like before.
  const resolved = resolveBrandMark()
  const [orgMarkIdx, setOrgMarkIdx] = useState(0)
  const [orgMarkDead, setOrgMarkDead] = useState(false)
  const brandMark = resolved.candidates && orgMarkDead ? resolved.fallback : resolved
  const brandMarkSrc = resolved.candidates && !orgMarkDead ? resolved.candidates[orgMarkIdx] : brandMark.src
  const onBrandMarkError = () => {
    if (!resolved.candidates || orgMarkDead) return
    if (orgMarkIdx + 1 < resolved.candidates.length) setOrgMarkIdx(orgMarkIdx + 1)
    else setOrgMarkDead(true)
  }

  const [userType, orgCode] = (() => {
    try { return [localStorage.getItem("user_type"), localStorage.getItem("user_org_code")] } catch (e) { return [null, null] }
  })()

  const canSee = (item) => {
    const pageGranted = (page) => userPageAccess.some((p) => p.value === page)
    if (item.adminOnly) return isAdmin || (userType === "CAM" && item.camPage && pageGranted(item.camPage))
    if (item.orgOnly && userType !== "org_admin") return false
    if (item.spearOnly && orgCode && orgCode !== "SPEARMGMT") return false
    if (item.restrictable && restrictedyn) return false
    return pageGranted(item.page)
  }

  const width = collapsed ? layout.sidebarWidthCollapsed : layout.sidebarWidth

  return (
    <Flex
      as="nav"
      direction="column"
      position="fixed"
      top={0}
      left={0}
      h="100vh"
      w={width}
      zIndex={1000}
      color={neutral[0]}
      transition={`width ${motion.slow}`}
      overflow="hidden"
      sx={{
        // navbar_bg.svg — generated by scripts/build-nav-background.js. Keeps
        // the flowing bands and geometric line-work of the original artwork,
        // but drawn dark enough to carry white labels at AA on its own, so no
        // scrim is painted over it. Vector, so it stays crisp at any viewport
        // height: 4.8 kB against the old PNG's 1.5 MB.
        backgroundImage: `url('/navbar_bg.svg')`,
        // Stretched rather than `cover`: the rail's aspect ratio swings from
        // ~0.15 to ~0.30 depending on viewport height, and `cover` cropped the
        // composition's foot off on shorter screens. The art is soft bands and
        // low-opacity line-work, so the slight non-uniform scale is invisible —
        // and the top crest and foot motif are then always both in frame.
        backgroundSize: "100% 100%",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundColor: brand[800],
      }}
    >
      <Box position="relative" flexShrink={0} borderBottom="1px solid rgba(255,255,255,0.12)">
        {/* The mark is Spear's unless the signed-in account is a white-label
            partner, in which case every screen they see carries theirs instead
            — see tools/brandMark.js. The two draw differently and must not share
            a code path: the Spear asset needs a crop, a partner asset does not. */}
        {brandMark.fit === "crop" ? (
          /* spearbi_ai4.png is the transparent script wordmark — the same file
             the landing navbar uses. It is a 500×500 square whose glyph occupies
             only the middle ~58% × ~48%, so drawing it with object-fit leaves it
             looking half-size. Oversizing it and letting the box clip trims the
             dead padding without needing a second cropped asset.
             (The rail previously loaded spearbi_ai1.jpg: 30123×25472px.) */
          <Box
            role="img"
            aria-label={brandMark.label}
            h={collapsed ? "58px" : "90px"}
            overflow="hidden"
            transition={`height ${motion.slow}`}
            filter="drop-shadow(0 2px 8px rgba(0,0,0,0.35))"
            sx={{
              backgroundImage: `url('${brandMark.src}')`,
              backgroundRepeat: "no-repeat",
              // 42% rather than centre: the glyph sits above the middle of the
              // source square, so a straight centre crop reads top-heavy.
              backgroundPosition: "center 42%",
              // Scaled per state: the glyph is ~58% of the square's width, so the
              // 64px collapsed rail needs a smaller multiplier or the wordmark
              // runs off both edges.
              backgroundSize: collapsed ? "auto 160%" : "auto 195%",
            }}
          />
        ) : (
          /* A partner asset arrives already trimmed and, so far, on an opaque
             light ground. Drawn whole, and on a plate when it needs one — a
             white JPEG laid straight on the dark rail reads as a hole in it. */
          <Flex
            align="center"
            justify="center"
            h={collapsed ? "58px" : "90px"}
            px={collapsed ? 1.5 : 3}
            py={collapsed ? 1.5 : 2.5}
            transition={`height ${motion.slow}`}
          >
            <Image
              key={brandMarkSrc}
              src={brandMarkSrc}
              onError={onBrandMarkError}
              alt={brandMark.label}
              maxH="100%"
              maxW="100%"
              objectFit="contain"
              bg={brandMark.plate ? neutral[0] : "transparent"}
              borderRadius={brandMark.plate ? radius.sm : undefined}
              p={brandMark.plate ? (collapsed ? 1 : 2) : 0}
              filter="drop-shadow(0 2px 8px rgba(0,0,0,0.35))"
            />
          </Flex>
        )}

        <IconButton
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          icon={collapsed ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
          onClick={onToggleCollapse}
          variant="ghost"
          size="xs"
          fontSize="11px"
          position="absolute"
          top="6px"
          right="6px"
          color="rgba(255,255,255,0.55)"
          _hover={{ bg: "rgba(255,255,255,0.16)", color: neutral[0] }}
          _focusVisible={{ outline: "2px solid rgba(255,255,255,0.7)", outlineOffset: "-2px" }}
        />
      </Box>

      {hasSuppliers && (
        <Box px={collapsed ? 1.5 : 3} py={3} flexShrink={0}>
          <SupplierNavigation collapsed={collapsed} />
        </Box>
      )}

      <Box
        flex="1"
        overflowY="auto"
        overflowX="hidden"
        px={collapsed ? 1.5 : 3}
        pb={4}
        css={{
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "3px" },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255,255,255,0.22)",
            borderRadius: "3px",
            border: "none",
          },
          "&::-webkit-scrollbar-track": { background: "transparent" },
        }}
      >
        <NavGroup
          title="Analytics"
          items={ANALYTICS.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="People & Access"
          items={PEOPLE_ACCESS.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Data"
          items={DATA_ADMIN.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="EDI & Automation"
          items={EDI_AUTOMATION.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Reports"
          items={REPORTS_ADMIN.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
        <NavGroup
          title="Account"
          items={ACCOUNT.filter(canSee)}
          pathname={pathname}
          collapsed={collapsed}
        />
      </Box>
    </Flex>
  )
}

export default Sidebar
